import React, { useEffect, useState } from "react";
import { ObserveState } from "../StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { SxProps, TextField } from "@mui/material";
import { getPropertyEditorValidationIssues, PropertyEditorValidationTooltip } from "./PropertyEditorValidationTooltip";

export interface PropertyEditorIntegerInterface {
    editObject: object;
    propertyName: string;
    label?: string;
    decimal?: boolean;
    hint?: string;
    parentDefaultValue?: number | null;
    autoFocus?: boolean;
    variant?: "filled" | "outlined" | "standard";
    max?: number;
    min?: number;
    onChange?: () => void;
    sx?: SxProps;
    error?: string;
    ignoreDesignerValidation?: boolean;
}

export const PropertyEditorInteger: React.FC<PropertyEditorIntegerInterface> = (
    props: PropertyEditorIntegerInterface,
) => {
    let [val, setVal] = useState<number>(Reflect.get(props.editObject, props.propertyName) ?? props.parentDefaultValue);

    useEffect(() => {
        setVal(Reflect.get(props.editObject, props.propertyName) ?? props.parentDefaultValue);
    }, [props.editObject, props.parentDefaultValue]);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            let val: number | undefined = undefined;
            if (props.decimal) {
                val = parseFloat(event.target.value);
            } else {
                val = parseInt(event.target.value, 10);
            }

            if (props.max !== undefined && val > props.max) {
                val = props.max;
            }
            if (props.min !== undefined && val < props.min) {
                val = props.min;
            }

            setVal(val);
        } catch {}
    };

    return (
        <ObserveState
            listenTo={props.editObject}
            properties={[props.propertyName]}
            control={() => {
                let variant = props.variant || "outlined";
                let validationIssues = getPropertyEditorValidationIssues(
                    props.editObject,
                    props.propertyName,
                    props.error,
                );

                return (
                    <PropertyEditorValidationTooltip
                        editObject={props.editObject}
                        propertyName={props.propertyName}
                        error={validationIssues}
                    >
                        <TextField
                            data-testid={"property-editor-integer"}
                            value={val || props.parentDefaultValue}
                            type="number"
                            label={props.label}
                            onChange={onChange}
                            size={"small"}
                            onBlur={() => {
                                if (props.parentDefaultValue && props.parentDefaultValue === val) {
                                    Reflect.set(props.editObject, props.propertyName, null);
                                } else {
                                    Reflect.set(props.editObject, props.propertyName, val);
                                }
                                StateManager.propertyChanged(props.editObject, props.propertyName);
                                if (props.onChange) props.onChange();
                            }}
                            helperText={validationIssues?.message ?? props.hint}
                            autoFocus={props.autoFocus}
                            variant={variant}
                            fullWidth={true}
                            error={Boolean(validationIssues)}
                            sx={{ ...props.sx }}
                        />
                    </PropertyEditorValidationTooltip>
                );
            }}
        />
    );
};
